// @tkos/shared-ui — rich-HTML sanitiser (XSS defence).
//
// Robust, parser-based allowlist via DOMPurify. This mirrors the SERVER
// allowlist (kitchenos-api → modules/supplier_offers_news.js `sanitise()`,
// which uses `sanitize-html`) tag-for-tag so client-side sanitisation matches
// the server's robustness. Replaces the previous weak regex blocklists that
// let unquoted event handlers (<img onerror=…>, <svg onload=…>), malformed or
// unclosed <script>/<iframe>, and entity/whitespace-obfuscated `javascript:`
// URLs slip through.
//
// Used for supplier offer/news rich content in two places:
//   • supplier editor — pre-send sanitise of the contentEditable output, and
//     re-sanitise when loading an existing item back into the editor;
//   • retailer dashboard — belt-and-braces before rendering server content
//     via innerHTML (the server remains authoritative on write).
//
// DOMPurify sanitises CSS inside the `style` attribute itself (stripping
// url(javascript:…)/expression()/etc.), so allowing `style` is safe and
// preserves the inline styling real offers rely on. `data:` URIs are permitted
// by DOMPurify only for <img> (the editor produces base64 data-URL images).
import DOMPurify, { type Config } from "dompurify";

const CONFIG: Config = {
  ALLOWED_TAGS: [
    "p", "div", "span", "br", "hr", "blockquote",
    "h1", "h2", "h3", "h4", "h5", "h6",
    "strong", "b", "em", "i", "u", "s",
    "ul", "ol", "li", "a", "img",
    "table", "thead", "tbody", "tr", "td", "th", "figure", "figcaption",
  ],
  ALLOWED_ATTR: [
    "href", "target", "rel",
    "src", "alt", "width", "height",
    "style",
  ],
};

// Harden outbound links (parity with the server): no window.opener handle, no
// referrer leak. Registered once at module load; scoped to <a> only.
let hookRegistered = false;
function ensureHook(): void {
  if (hookRegistered) return;
  DOMPurify.addHook("afterSanitizeAttributes", (node) => {
    // Harden outbound links: no window.opener handle, no referrer leak.
    if (node.tagName === "A" && node.hasAttribute("href")) {
      node.setAttribute("rel", "noopener noreferrer nofollow");
    }
    // Strip dangerous CSS from inline styles (parity with the server, whose
    // style-value allowlist rejects these). DOMPurify keeps the `style`
    // attribute but does not filter url()/expression()/@import inside it;
    // legitimate offers use none of these (colours/spacing/fonts only), so
    // dropping any offending declaration is loss-free.
    if (node.hasAttribute("style")) {
      const style = node.getAttribute("style") || "";
      if (/url\(|expression\(|javascript:|@import/i.test(style)) {
        const cleaned = style
          .split(";")
          .filter((decl) => !/url\(|expression\(|javascript:|@import/i.test(decl))
          .join(";")
          .trim();
        if (cleaned) node.setAttribute("style", cleaned);
        else node.removeAttribute("style");
      }
    }
  });
  hookRegistered = true;
}

/** Sanitise rich HTML (supplier offer/news bodies) against the shared
 *  allowlist. Returns a safe HTML string. */
export function sanitiseRichHtml(html: string): string {
  ensureHook();
  return DOMPurify.sanitize(String(html || ""), CONFIG);
}
